/**
 * API Client
 *
 * Centralized HTTP client for communicating with the Bun.js backend.
 * Automatically attaches the auth token to all requests.
 *
 * The token is managed by AuthContext via setAuthToken().
 * This decouples the API client from the Supabase client.
 *
 * When a 401 response is received, the registered onUnauthorized callback
 * is invoked so AuthContext can clear the session and redirect to login.
 */

// ============================================================================
// TOKEN MANAGEMENT (set by AuthContext)
// ============================================================================

let _authToken: string | null = null;

/**
 * Set the auth token. Called by AuthContext on login, logout, and session restore.
 */
export const setAuthToken = (token: string | null) => {
  _authToken = token;
};

// ============================================================================
// 401 INTERCEPTOR (registered by AuthContext)
// ============================================================================

type UnauthorizedCallback = () => void;
let _onUnauthorized: UnauthorizedCallback | null = null;

/**
 * Register a callback to be called when a 401 Unauthorized response is received.
 * AuthContext uses this to clear the session and trigger a redirect to /login.
 */
export const setOnUnauthorized = (cb: UnauthorizedCallback | null) => {
  _onUnauthorized = cb;
};

// ============================================================================
// PAYWALL INTERCEPTOR (registered by UserStatusContext)
// ============================================================================

type PaywallCallback = () => void;
let _onPaywallRequired: PaywallCallback | null = null;

/**
 * Register a callback to be called when a 403 PAYWALL_REQUIRED response is received.
 * UserStatusContext uses this to show the paywall modal.
 */
export const setOnPaywallRequired = (cb: PaywallCallback | null) => {
  _onPaywallRequired = cb;
};

// ============================================================================
// PASSWORD-CHANGE INTERCEPTOR (registered by AuthContext)
// ============================================================================

/** Código do 403 da API enquanto a senha temporária não for trocada. */
export const MUST_CHANGE_PASSWORD_CODE = 'must_change_password';

type PasswordChangeCallback = () => void;
let _onPasswordChangeRequired: PasswordChangeCallback | null = null;

/**
 * Register a callback for 403 must_change_password (temporary password set by
 * support). AuthContext flags the user so ProtectedRoute redirects to
 * /change-password — covers calls made before /api/user/status loads.
 */
export const setOnPasswordChangeRequired = (cb: PasswordChangeCallback | null) => {
  _onPasswordChangeRequired = cb;
};

export function isMustChangePasswordResponse(status: number, body: { code?: unknown } | null | undefined): boolean {
  return status === 403 && body?.code === MUST_CHANGE_PASSWORD_CODE;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  retryable?: boolean;
  details?: unknown;

  constructor(
    message: string,
    options: { status: number; code?: string; retryable?: boolean; details?: unknown }
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.retryable = options.retryable;
    this.details = options.details;
  }
}

/**
 * Get the base URL for API calls.
 * In development with Vite proxy, use relative paths.
 * In production, use the configured API URL.
 */
const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_URL || '';
};

/**
 * Get the current auth token (synchronous - no Supabase dependency).
 */
const getAuthToken = (): string | null => {
  return _authToken;
};

const DEFAULT_TIMEOUT_MS = 120_000; // 2 minutes

/**
 * Make an authenticated API request.
 * @param timeoutMs - Request timeout in milliseconds (default: 120s).
 *                    Pass 0 to disable timeout.
 */
export async function apiFetch<T = any>(
  path: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<T> {
  const token = getAuthToken();
  const baseUrl = getApiBaseUrl();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let controller: AbortController | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  if (timeoutMs > 0 && !options.signal) {
    controller = new AbortController();
    timeoutId = setTimeout(() => controller!.abort(), timeoutMs);
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers,
      signal: controller?.signal ?? options.signal,
    });
  } catch (err: any) {
    if (timeoutId) clearTimeout(timeoutId);
    if (err?.name === 'AbortError') {
      throw new ApiError(
        'A requisição excedeu o tempo limite. Tente novamente.',
        { status: 408, code: 'TIMEOUT', retryable: true }
      );
    }
    throw err;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  const rawBody = await response.text().catch(() => '');
  let parsedBody: Record<string, any> = {};
  if (rawBody) {
    try {
      parsedBody = JSON.parse(rawBody) as Record<string, any>;
    } catch {
      // Response is not JSON — likely an HTML error page from Cloudflare/proxy
      if (rawBody.trimStart().startsWith('<!') || rawBody.trimStart().startsWith('<html')) {
        const titleMatch = rawBody.match(/<title[^>]*>([^<]*)<\/title>/i);
        const statusHint = titleMatch?.[1] || '';
        const isTimeout = response.status === 504 || response.status === 502 || statusHint.toLowerCase().includes('timeout');
        parsedBody = {
          error: isTimeout
            ? 'O servidor demorou para responder. Tente novamente em instantes.'
            : `Erro de comunicação com o servidor (${response.status}). Tente novamente.`,
          retryable: isTimeout,
        };
      } else {
        // Non-HTML, non-JSON — truncate to avoid showing garbage
        parsedBody = { error: rawBody.length > 200 ? rawBody.substring(0, 200) + '...' : rawBody };
      }
    }
  }

  if (!response.ok) {
    const errorMessage = parsedBody.error || response.statusText || `API error: ${response.status}`;

    // If 401 Unauthorized, notify AuthContext to clear session and redirect
    if (response.status === 401 && _onUnauthorized) {
      _onUnauthorized();
    }

    // If 403 must_change_password, notify AuthContext to force /change-password
    if (isMustChangePasswordResponse(response.status, parsedBody) && _onPasswordChangeRequired) {
      _onPasswordChangeRequired();
    }

    // If 403 PAYWALL_REQUIRED or LIMIT_EXCEEDED, notify UserStatusContext to show paywall modal
    if (response.status === 403 && (parsedBody.code === 'PAYWALL_REQUIRED' || parsedBody.code === 'LIMIT_EXCEEDED') && _onPaywallRequired) {
      _onPaywallRequired();
    }

    throw new ApiError(errorMessage, {
      status: response.status,
      code: typeof parsedBody.code === 'string' ? parsedBody.code : undefined,
      retryable: typeof parsedBody.retryable === 'boolean' ? parsedBody.retryable : undefined,
      details: parsedBody,
    });
  }

  if (!rawBody) {
    return {} as T;
  }

  try {
    return JSON.parse(rawBody) as T;
  } catch {
    throw new ApiError('Invalid JSON response from API.', {
      status: response.status,
      details: rawBody,
    });
  }
}

/**
 * POST request helper.
 * @param timeoutMs - Optional timeout override (default: 120s).
 */
export function apiPost<T = any>(path: string, body: Record<string, any>, timeoutMs?: number): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  }, timeoutMs);
}

/**
 * GET request helper with optional query parameters.
 */
export function apiGet<T = any>(path: string, params?: Record<string, string>): Promise<T> {
  const url = params
    ? `${path}?${new URLSearchParams(params).toString()}`
    : path;
  return apiFetch<T>(url, { method: 'GET' });
}
